import { useState, useEffect } from 'react';
import { PageHeader, Card, LoadingSpinner, StatusBadge } from '../../components/Layout';
import api from '../../services/api';

export function HRDashboardPage() {
  const [loading, setLoading] = useState(true);
  const [pendingRequests, setPendingRequests] = useState<any[]>([]);
  const [todayShifts, setTodayShifts] = useState<any[]>([]);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      // Use Central Time to match the factory's operating day, not the browser's UTC
      const ctFormatter = new Intl.DateTimeFormat('en-CA', {
        timeZone: 'America/Chicago',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
      });
      const today = ctFormatter.format(new Date()); // YYYY-MM-DD in CT
      const [requests, shifts] = await Promise.all([
        api.getTimeOffRequests({ status: 'pending' }),
        api.getShiftsForDate(today),
      ]);
      setPendingRequests(requests || []);
      setTodayShifts(shifts || []);
    } catch (error) {
      console.error('Error loading HR data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (id: string) => {
    if (!confirm('Approve this time off request?')) return;
    try {
      await api.approveTimeOffRequest(id);
      alert('Request approved successfully');
      loadData();
    } catch (error: any) {
      alert(error.message || 'Failed to approve request');
    }
  };

  const handleDeny = async (id: string) => {
    const reason = prompt('Enter reason for denial:');
    if (!reason) return;
    try {
      await api.denyTimeOffRequest(id, reason);
      alert('Request denied');
      loadData();
    } catch (error: any) {
      alert(error.message || 'Failed to deny request');
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-96">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        title="HR Dashboard"
        subtitle="Manage employee schedules, time off, and more"
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
        <Card>
          <div className="text-center">
            <div className="text-gray-500 text-sm">Pending Time Off</div>
            <div className="text-3xl font-bold text-blue-600 mt-2">{pendingRequests.length}</div>
          </div>
        </Card>
        <Card>
          <div className="text-center">
            <div className="text-gray-500 text-sm">Working Today</div>
            <div className="text-3xl font-bold text-green-600 mt-2">{todayShifts.length}</div>
          </div>
        </Card>
        <Card>
          <div className="text-center">
            <div className="text-gray-500 text-sm">Total Employees</div>
            <div className="text-3xl font-bold text-gray-900 mt-2">--</div>
          </div>
        </Card>
      </div>

      {/* Pending Time Off Requests */}
      <Card className="mb-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Pending Time Off Requests</h2>
        {pendingRequests.length === 0 ? (
          <p className="text-gray-500 text-center py-8">No pending requests</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="text-left px-4 py-3 text-sm font-medium text-gray-700">Employee</th>
                  <th className="text-left px-4 py-3 text-sm font-medium text-gray-700">Type</th>
                  <th className="text-left px-4 py-3 text-sm font-medium text-gray-700">Dates</th>
                  <th className="text-left px-4 py-3 text-sm font-medium text-gray-700">Hours</th>
                  <th className="text-left px-4 py-3 text-sm font-medium text-gray-700">Reason</th>
                  <th className="text-right px-4 py-3 text-sm font-medium text-gray-700">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {pendingRequests.map((request) => (
                  <tr key={request.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm text-gray-900">{request.employee_name}</td>
                    <td className="px-4 py-3 text-sm">
                      <span className="capitalize">{request.request_type}</span>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">
                      {new Date(request.start_date).toLocaleDateString()} - {new Date(request.end_date).toLocaleDateString()}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">{request.total_hours}</td>
                    <td className="px-4 py-3 text-sm text-gray-600">{request.reason || '--'}</td>
                    <td className="px-4 py-3 text-sm text-right space-x-2">
                      <button
                        onClick={() => handleApprove(request.id)}
                        className="text-green-600 hover:text-green-700 font-medium"
                      >
                        Approve
                      </button>
                      <button
                        onClick={() => handleDeny(request.id)}
                        className="text-red-600 hover:text-red-700 font-medium"
                      >
                        Deny
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* Today's Shifts */}
      <Card>
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Today's Shifts</h2>
        {todayShifts.length === 0 ? (
          <p className="text-gray-500 text-center py-8">No shifts scheduled today</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="text-left px-4 py-3 text-sm font-medium text-gray-700">Employee</th>
                  <th className="text-left px-4 py-3 text-sm font-medium text-gray-700">Time</th>
                  <th className="text-left px-4 py-3 text-sm font-medium text-gray-700">Status</th>
                  <th className="text-left px-4 py-3 text-sm font-medium text-gray-700">Clock In</th>
                  <th className="text-left px-4 py-3 text-sm font-medium text-gray-700">Clock Out</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {todayShifts.map((shift) => (
                  <tr key={shift.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm text-gray-900">{shift.employee_name}</td>
                    <td className="px-4 py-3 text-sm text-gray-600">
                      {shift.start_time} - {shift.end_time}
                    </td>
                    <td className="px-4 py-3 text-sm">
                      <StatusBadge status={shift.status} />
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">
                      {shift.clock_in_time ? new Date(shift.clock_in_time).toLocaleTimeString() : '--'}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">
                      {shift.clock_out_time ? new Date(shift.clock_out_time).toLocaleTimeString() : '--'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}
